/**
 * Sandbox workspace + demo-location APIs (AES-278).
 * Browser talks only to the portal — never Signup / Desk / integrators.
 */

import apiClient from "@/lib/api/api-client";

export type StudioType = "passionfitness" | "flexandflow";

export type DemoLocationStatus = "creating" | "ready" | "failed";

export type SandboxCredentialsStatus =
  | "none"
  | "pending"
  | "issued"
  | "failed";

export type DemoLocation = {
  id: string;
  sandbox_workspace_id: string;
  display_name: string;
  studio_type: StudioType;
  admin_email: string;
  branch_id?: string | null;
  namespace?: string | null;
  client_id?: string | null;
  status: DemoLocationStatus;
  failure_step?: string | null;
  failure_reason?: string | null;
  /** Present only when status is `ready` (owner GET). */
  admin_password?: string | null;
  created_at: string;
  updated_at: string;
};

export type SandboxWorkspace = {
  id: string;
  access_request_id: string;
  portal_user_id: string;
  credentials_status: SandboxCredentialsStatus;
  /**
   * Existing Glofox integrator id when set (AES-359+ import or after first
   * credentials issue). Optional until the backend exposes it on GET.
   */
  integrator_id?: string | null;
  locations: DemoLocation[];
  created_at: string;
  updated_at: string;
};

export type ListSandboxWorkspacesResponse = {
  workspaces: SandboxWorkspace[];
};

export type CreateDemoLocationRequest = {
  admin_password: string;
  studio_type: StudioType;
};

export type CreateDemoLocationResponse = {
  id: string;
  status: DemoLocationStatus;
};

export type RequestSandboxCredentialsResponse = {
  status: SandboxCredentialsStatus;
  message: string;
};

export const STUDIO_TYPE_OPTIONS: ReadonlyArray<{
  value: StudioType;
  label: string;
}> = [
  { value: "passionfitness", label: "Passion Fitness" },
  { value: "flexandflow", label: "Flex and Flow" },
];

/**
 * Studio type used for every portal-created test location. The demo data set
 * does not change how integrators call the API, so the portal does not ask.
 */
export const DEFAULT_STUDIO_TYPE: StudioType = "passionfitness";

const SANDBOX_WORKSPACES_PATH = "/api/v1/sandbox/workspaces";

export async function listSandboxWorkspaces(): Promise<ListSandboxWorkspacesResponse> {
  const { data } = await apiClient.get<ListSandboxWorkspacesResponse>(
    SANDBOX_WORKSPACES_PATH,
  );
  return data;
}

export async function getSandboxWorkspace(
  workspaceId: string,
): Promise<SandboxWorkspace> {
  const { data } = await apiClient.get<SandboxWorkspace>(
    `${SANDBOX_WORKSPACES_PATH}/${workspaceId}`,
  );
  return data;
}

export async function createSandboxDemoLocation(
  workspaceId: string,
  payload: CreateDemoLocationRequest,
): Promise<CreateDemoLocationResponse> {
  const { data } = await apiClient.post<CreateDemoLocationResponse>(
    `${SANDBOX_WORKSPACES_PATH}/${workspaceId}/demo-locations`,
    payload,
    { validateStatus: (status) => status === 202 },
  );
  return data;
}

export async function getSandboxDemoLocation(
  workspaceId: string,
  locationId: string,
): Promise<DemoLocation> {
  const { data } = await apiClient.get<DemoLocation>(
    `${SANDBOX_WORKSPACES_PATH}/${workspaceId}/demo-locations/${locationId}`,
  );
  return data;
}

/** Issues or resends sandbox API credentials via the portal backend. */
export async function requestSandboxCredentials(
  workspaceId: string,
): Promise<RequestSandboxCredentialsResponse> {
  const { data } = await apiClient.post<RequestSandboxCredentialsResponse>(
    `${SANDBOX_WORKSPACES_PATH}/${workspaceId}/credentials`,
  );
  return data;
}

export function findWorkspaceForAccessRequest(
  workspaces: SandboxWorkspace[],
  accessRequestId: string,
): SandboxWorkspace | undefined {
  return workspaces.find(
    (workspace) => workspace.access_request_id === accessRequestId,
  );
}
